import './claim-bordereau-modal.scss';

import {
  Box,
  Typography,
  Modal,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { NewConvergeButton } from '../../../presentation/components/new-button';
import { Close } from '@mui/icons-material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import React, { useState } from 'react';
import { useBordereauMutation } from '@converge/graphql';
import { useConvergeSnackBar } from '../../../contexts';

interface ClaimBordereauModalProps {
  open: boolean;
  onClose: () => void;
}

const ClaimBordereauModal = ({ open, onClose }: ClaimBordereauModalProps) => {
  const [statsDateMode, setStatsDateMode] = useState<'single' | 'range'>(
    'range'
  );
  const [statsSingleDate, setStatsSingleDate] = useState<Dayjs | null>(null);
  const [statsDateFrom, setStatsDateFrom] = useState<Dayjs | null>(null);
  const [statsDateTo, setStatsDateTo] = useState<Dayjs | null>(null);

  const { generateReport, loading: bordereauLoading } = useBordereauMutation();
  const { notify } = useConvergeSnackBar();

  const handleDownloadReport = () => {
    const details =
      statsDateMode === 'single'
        ? { asOfDate: statsSingleDate?.format('YYYY-MM-DD') }
        : {
            startDate: statsDateFrom?.format('YYYY-MM-DD'),
            endDate: statsDateTo?.format('YYYY-MM-DD'),
          };

    onClose();
    notify(
      'Your custom bordereau is being generated. It will download automatically when it is complete.',
      {
        variant: 'info',
      }
    );

    generateReport({ variables: { details } })
      .then(
        ({
          data,
        }: {
          data?: {
            generateBordereauClaimReport?: {
              file?: string;
              contentType?: string;
              filename?: string;
            };
          };
        }) => {
          const report = data?.generateBordereauClaimReport;
          if (report?.file) {
            const byteCharacters = window.atob(report.file);
            const byteArray = new Uint8Array(
              Array.from(byteCharacters, (c) => c.charCodeAt(0))
            );
            const blob = new Blob([byteArray], {
              type: report.contentType || 'application/octet-stream',
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = report.filename || 'bordereau-report';
            a.click();
            URL.revokeObjectURL(url);
            notify('Your custom bordereau report has been downloaded.', {
              variant: 'success',
            });
          }
        }
      )
      .catch((err: Error) => {
        console.error('Failed to generate bordereau report', err);
        notify('Failed to generate report. Please try again.', {
          variant: 'error',
        });
      });
  };

  const isDownloadDisabled =
    bordereauLoading ||
    (statsDateMode === 'single'
      ? !statsSingleDate
      : !statsDateFrom || !statsDateTo);

  const rangeDisabled = statsDateMode !== 'range';
  const singleDisabled = statsDateMode !== 'single';
  const pickerClass = (disabled: boolean) =>
    `claim-bordereau-modal-picker-wrapper${
      disabled ? ' claim-bordereau-modal-picker-wrapper--disabled' : ''
    }`;
  return (
    <Modal open={open} onClose={onClose} data-testid="bordereau-modal">
      <Box className="claim-bordereau-modal-box">
        <Box className="claim-bordereau-modal-header">
          <Typography
            className="claim-bordereau-modal-title"
            data-testid="bordereau-modal-title"
          >
            Create Custom Bordereau Report
          </Typography>
          <IconButton onClick={onClose} data-testid="close-modal-button">
            <Close />
          </IconButton>
        </Box>
        <Typography
          className="claim-bordereau-modal-subtitle"
          data-testid="bordereau-modal-subtitle"
        >
          Select the date range or as of date for your custom bordereau report.
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box className="claim-bordereau-modal-main-container">
            <RadioGroup
              value={statsDateMode}
              onChange={(e) =>
                setStatsDateMode(e.target.value as 'single' | 'range')
              }
            >
              <FormControlLabel
                value="range"
                className="claim-bordereau-modal-radio-label"
                control={
                  <Radio
                    size="medium"
                    inputProps={
                      {
                        'data-testid': 'date-range-radio',
                      } as React.InputHTMLAttributes<HTMLInputElement>
                    }
                  />
                }
                label="Date range"
              />
              <Box className="claim-bordereau-modal-date-row">
                <Box className="claim-bordereau-modal-date-col">
                  <Typography className="claim-bordereau-modal-date-label">
                    Start Date
                  </Typography>
                  <Box className={pickerClass(rangeDisabled)}>
                    <DatePicker
                      value={statsDateFrom}
                      components={{ OpenPickerIcon: DateRangeIcon }}
                      onChange={(val) => {
                        setStatsDateFrom(val);
                        if (val && statsDateTo && val.isAfter(statsDateTo))
                          setStatsDateTo(null);
                      }}
                      disabled={rangeDisabled}
                      maxDate={dayjs()}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          inputProps={{
                            ...params.inputProps,
                            placeholder: 'MM/DD/YYYY',
                            'data-testid': 'start-date-input',
                          }}
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                </Box>
                <Box className="claim-bordereau-modal-date-col">
                  <Typography className="claim-bordereau-modal-date-label">
                    End Date
                  </Typography>
                  <Box className={pickerClass(rangeDisabled)}>
                    <DatePicker
                      value={statsDateTo}
                      components={{ OpenPickerIcon: DateRangeIcon }}
                      onChange={(val) => setStatsDateTo(val)}
                      disabled={rangeDisabled}
                      minDate={statsDateFrom ?? undefined}
                      maxDate={dayjs()}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          inputProps={{
                            ...params.inputProps,
                            placeholder: 'MM/DD/YYYY',
                            'data-testid': 'end-date-input',
                          }}
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                </Box>
              </Box>

              <FormControlLabel
                value="single"
                className="claim-bordereau-modal-radio-label"
                control={
                  <Radio
                    size="medium"
                    inputProps={
                      {
                        'data-testid': 'as-of-date-radio',
                      } as React.InputHTMLAttributes<HTMLInputElement>
                    }
                  />
                }
                label="As of date"
              />
              <Box className="claim-bordereau-modal-as-of-row">
                <Box className="claim-bordereau-modal-date-col">
                  <Typography className="claim-bordereau-modal-date-label">
                    As of Date
                  </Typography>
                  <Box className={pickerClass(singleDisabled)}>
                    <DatePicker
                      value={statsSingleDate}
                      components={{ OpenPickerIcon: DateRangeIcon }}
                      onChange={(val) => setStatsSingleDate(val)}
                      disabled={singleDisabled}
                      maxDate={dayjs()}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          inputProps={{
                            ...params.inputProps,
                            placeholder: 'MM/DD/YYYY',
                            'data-testid': 'as-of-date-input',
                          }}
                          size="small"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                </Box>
              </Box>
            </RadioGroup>
          </Box>
        </LocalizationProvider>

        <Box className="claim-bordereau-modal-footer">
          <NewConvergeButton
            label="CANCEL"
            variant="outlined"
            onClick={onClose}
            dataTestId="cancel-button"
          />
          <Box>
            <NewConvergeButton
              label={bordereauLoading ? 'GENERATING...' : 'DOWNLOAD REPORT'}
              variant="contained"
              disabled={isDownloadDisabled}
              onClick={handleDownloadReport}
              dataTestId="download-report-button"
            />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ClaimBordereauModal;
